/*
 * CONFIDENTIAL & PROPRIETARY
 *
 * Copyright © 2025 AionTrack. All rights reserved.
 *
 * This file contains proprietary algorithms and trade secrets.
 * Unauthorized use, disclosure, or reproduction is strictly prohibited.
 *
 * File: TimelineVisualization.tsx
 * Contains: NLE-style timeline visualization with interactive commit diff rendering
 * and frame-accurate navigation
 * Classification: TRADE SECRET - DO NOT DISTRIBUTE
 */

/**
 * NLE-Style Timeline Visualization Component
 *
 * Displays commit history as rectangular blocks on a horizontal timeline,
 * similar to Premiere Pro's clip timeline. Supports click selection, hover tooltips,
 * panning, and keyboard navigation.
 */

import { useState, useRef, useEffect, useMemo } from 'react'
import { formatDistanceToNow } from 'date-fns'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

// =============================================
// TYPES
// =============================================

export interface Commit {
  id: string
  message: string
  timestamp: Date
  author: string
  changeCount: { added: number; removed: number; trimmed: number; moved: number }
  otio_timeline?: string // OTIO JSON to extract timeline name
}

export interface TimelineVisualizationProps {
  commits: Commit[]
  selectedCommitId: string | null
  onSelectCommit: (commitId: string) => void
  className?: string
}

// =============================================
// HELPER FUNCTIONS
// =============================================

/**
 * Extract timeline name from OTIO JSON
 */
function getTimelineName(otioJson?: string): string | null {
  if (!otioJson) return null

  try {
    const timeline = typeof otioJson === 'string' ? JSON.parse(otioJson) : otioJson
    return timeline.name || null
  } catch (error) {
    console.error('Failed to parse OTIO timeline:', error)
    return null
  }
}

/**
 * Get Tailwind CSS classes for commit block (simplified colors)
 */
function getBlockColorClasses(isSelected: boolean): string {
  const baseClasses = 'transition-all duration-200'

  if (isSelected) {
    // Selected state with primary color
    return `${baseClasses} bg-primary border-primary/50 border-2 shadow-lg`
  }

  // Non-selected state with muted colors
  return `${baseClasses} bg-secondary/80 border-border border hover:bg-secondary hover:border-border/80`
}

/**
 * Format change count for display (e.g., "+5 -2")
 */
function formatChangeCount(changeCount: Commit['changeCount']): string {
  const { added, removed } = changeCount
  const parts: string[] = []

  if (added > 0) parts.push(`+${added}`)
  if (removed > 0) parts.push(`-${removed}`)

  return parts.join(' ') || '~0' // ~0 for modifications only
}

// =============================================
// MAIN COMPONENT
// =============================================

export function TimelineVisualization({
  commits,
  selectedCommitId,
  onSelectCommit,
  className = '',
}: TimelineVisualizationProps) {
  // State for timeline panning
  const [isDragging, setIsDragging] = useState(false)
  const [dragStartX, setDragStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)

  // Refs
  const timelineRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Constants
  const BLOCK_WIDTH = 200 // px
  const BLOCK_HEIGHT = 80 // px
  // const BLOCK_SPACING = 16 // px // Unused for now
  const PLAYHEAD_HEIGHT = 100 // px

  // Memoize selected commit index for playhead position
  const selectedIndex = useMemo(() => {
    return commits.findIndex(c => c.id === selectedCommitId)
  }, [commits, selectedCommitId])

  // Calculate playhead position (center of selected block)
  // Unused for now but kept for future playhead visualization
  // const playheadPosition = selectedIndex >= 0
  //   ? (BLOCK_WIDTH + BLOCK_SPACING) * selectedIndex + BLOCK_WIDTH / 2
  //   : -1000 // Off-screen when nothing selected

  // =============================================
  // KEYBOARD NAVIGATION
  // =============================================

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (commits.length === 0) return

      const currentIndex = selectedIndex

      if (e.key === 'ArrowLeft' && currentIndex > 0) {
        // Move to previous commit
        onSelectCommit(commits[currentIndex - 1].id)
        e.preventDefault()
      } else if (e.key === 'ArrowRight' && currentIndex < commits.length - 1) {
        // Move to next commit
        onSelectCommit(commits[currentIndex + 1].id)
        e.preventDefault()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [commits, selectedIndex, onSelectCommit])

  // =============================================
  // DRAG TO PAN
  // =============================================

  function handleMouseDown(e: React.MouseEvent) {
    if (!timelineRef.current) return

    // Only start drag on timeline background, not on blocks
    if ((e.target as HTMLElement).closest('[data-commit-block]')) return

    setIsDragging(true)
    setDragStartX(e.clientX)
    setScrollLeft(timelineRef.current.scrollLeft)
  }

  function handleMouseMove(e: React.MouseEvent) {
    if (!isDragging || !timelineRef.current) return

    const deltaX = e.clientX - dragStartX
    timelineRef.current.scrollLeft = scrollLeft - deltaX
  }

  function handleMouseUp() {
    setIsDragging(false)
  }

  // =============================================
  // EMPTY STATE
  // =============================================

  if (commits.length === 0) {
    return (
      <div className={`flex items-center justify-center h-32 bg-secondary/50 rounded-lg border border-border ${className}`}>
        <div className="text-center text-muted-foreground">
          <p className="font-medium">No commits yet</p>
          <p className="text-sm mt-1">Upload a timeline to create your first commit</p>
        </div>
      </div>
    )
  }

  // =============================================
  // RENDER TIMELINE
  // =============================================

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Timeline Container */}
      <div
        ref={timelineRef}
        className={`
          overflow-x-auto overflow-y-hidden
          bg-secondary/30 rounded-lg
          border border-border
          ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}
          select-none
        `}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{ height: PLAYHEAD_HEIGHT + 40 }} // Extra padding for playhead
      >
        {/* Timeline Track */}
        <div className="relative py-5" style={{ minWidth: 'max-content' }}>
          {/* Commit Blocks */}
          <div className="flex items-start gap-4 px-4">
            {commits.map((commit) => {
              const isSelected = commit.id === selectedCommitId
              const relativeTime = formatDistanceToNow(commit.timestamp, { addSuffix: true })
              const timelineName = getTimelineName(commit.otio_timeline)

              return (
                <TooltipProvider key={commit.id}>
                  <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild>
                      <div
                        data-commit-block
                        className={`
                          relative flex-shrink-0 rounded-md p-3
                          cursor-pointer
                          ${getBlockColorClasses(isSelected)}
                        `}
                        style={{
                          width: BLOCK_WIDTH,
                          height: BLOCK_HEIGHT,
                        }}
                        onClick={() => onSelectCommit(commit.id)}
                      >
                        {/* Timeline Name (from OTIO) */}
                        <div className="text-sm font-semibold text-foreground line-clamp-1 mb-1">
                          {timelineName || 'Unnamed Timeline'}
                        </div>

                        {/* Commit Message (description) */}
                        <div className="text-xs text-muted-foreground line-clamp-2 mb-2">
                          {commit.message}
                        </div>

                        {/* Metadata Row */}
                        <div className="absolute bottom-2 left-3 right-3 flex items-center justify-between text-xs text-muted-foreground">
                          <span className="truncate">{relativeTime}</span>
                          <span className="font-mono font-semibold">
                            {formatChangeCount(commit.changeCount)}
                          </span>
                        </div>
                      </div>
                    </TooltipTrigger>

                    {/* Hover Tooltip */}
                    <TooltipContent side="top" className="max-w-sm">
                      <div className="space-y-1">
                        <p className="font-semibold">{timelineName || 'Unnamed Timeline'}</p>
                        <p className="text-sm">{commit.message}</p>
                        <p className="text-xs text-muted-foreground">by {commit.author}</p>
                        <p className="text-xs text-muted-foreground">{relativeTime}</p>
                        <div className="text-xs text-muted-foreground pt-1 border-t">
                          {commit.changeCount.added > 0 && <div>Added: {commit.changeCount.added} clips</div>}
                          {commit.changeCount.removed > 0 && <div>Removed: {commit.changeCount.removed} clips</div>}
                          {commit.changeCount.trimmed > 0 && <div>Trimmed: {commit.changeCount.trimmed} clips</div>}
                          {commit.changeCount.moved > 0 && <div>Moved: {commit.changeCount.moved} clips</div>}
                        </div>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )
            })}
          </div>
        </div>
      </div>

      {/* Timeline Instructions */}
      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
        <div className="ml-auto text-muted-foreground/60">
          Use arrow keys to navigate • Click and drag to pan
        </div>
      </div>
    </div>
  )
}
